// apps/client/src/game/interact.ts — the A-button contract (§8.7) and the interaction system (§8.3 slot `interaction`).
// Features add `game/interactions/<feature>.ts` (`export const interaction: Interaction`); M0 registers none, so A reads `hud.a`.
import type { StringKey } from '@whiskerton/content';
import type { CatalogId, SpaceId, Vec2 } from '@whiskerton/shared';
import type { Frame, System } from '../engine/systems';
import { useGame } from '../state/store';
import type { GameStore } from '../state/types';
import type { PickHit } from '../world/picking';
import type { Game } from './game';
import { interactionModules } from './interactions.gen';

export interface InteractCtx { game: Game; pos: Vec2; f: number; space: SpaceId; tool: CatalogId | null; store: GameStore }
export interface Candidate { id: string; at: Vec2; verb: StringKey; priority: number }
export interface Interaction {
  id: string;
  find(ctx: InteractCtx): Candidate | null;              // best target within tuning.town.interactRange, in front of you
  run(ctx: InteractCtx, c: Candidate): void;             // usually game.cmd.act(…) with an optimistic animation
  fromTap?(ctx: InteractCtx, hit: PickHit): Candidate | null;   // tap-to-walk: walk next to it, then run
}

const registered = (): readonly Interaction[] => (interactionModules as readonly { interaction: Interaction }[]).map((m) => m.interaction);

export class InteractionSystem implements System {
  readonly name = 'interaction' as const;
  private readonly game: Game;
  private best: { c: Candidate; i: Interaction } | null = null;
  private readonly owners = new WeakMap<Candidate, Interaction>();
  constructor(game: Game) { this.game = game; }
  ctx(): InteractCtx | null {
    const me = this.game.you();
    if (!me) return null;
    const store = useGame.getState();
    return { game: this.game, pos: me.pos, f: me.f, space: me.space, tool: store.tool, store };
  }
  /** Picks the highest-priority candidate (ties: the nearest) and writes `prompt` only when it changes. */
  update(_f: Frame): void {
    const ctx = this.ctx();
    let best: { c: Candidate; i: Interaction; d: number } | null = null;
    if (ctx) for (const i of registered()) {
      const c = i.find(ctx);
      if (!c) continue;
      const d = Math.hypot(c.at.x - ctx.pos.x, c.at.y - ctx.pos.y);
      if (!best || c.priority > best.c.priority || (c.priority === best.c.priority && d < best.d)) best = { c, i, d };
    }
    this.best = best ? { c: best.c, i: best.i } : null;
    const prompt = best ? { verb: best.c.verb, candidateId: best.c.id } : null;
    const cur = useGame.getState().prompt;
    if (cur?.verb !== prompt?.verb || cur?.candidateId !== prompt?.candidateId) useGame.setState({ prompt });
  }
  /** The A button: runs the kept candidate. False when nothing is in front of you. */
  runBest(): boolean {
    const ctx = this.ctx();
    if (!ctx || !this.best) return false;
    this.best.i.run(ctx, this.best.c);
    return true;
  }
  /** A tap on the world: the first interaction that claims the hit. */
  fromTap(hit: PickHit): Candidate | null {
    const ctx = this.ctx();
    if (!ctx) return null;
    for (const i of registered()) {
      const c = i.fromTap?.(ctx, hit) ?? null;
      if (c) { this.owners.set(c, i); return c; }
    }
    return null;
  }
  /** Runs a candidate fromTap returned (after tap-to-walk arrived next to it). */
  runCandidate(c: Candidate): boolean {
    const ctx = this.ctx(), i = this.owners.get(c);
    if (!ctx || !i) return false;
    i.run(ctx, c);
    return true;
  }
  clear(): void {
    this.best = null;
    if (useGame.getState().prompt) useGame.setState({ prompt: null });
  }
}
